/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import Helmet from 'react-helmet';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { hideElementCss, showElementCss } from '../../../utils/responsive';
import Spacer from '../../ui/Spacer';
import ImageGallery from './ImageGallery';
import Headline from './Headline';
import Solutions from './Solutions';
import Form from './Form';
import Stats from './Stats';
import Content from './Content';
import MediaQueries from '../../../styles/Grid/MediaQueries';
import { Container, Row, Column } from '../../../styles/Grid';
import { withConsumer } from '../../../contexts';
import { StickyBarConsumer } from '../../../contexts/StickyBarContext';
import {
  containerMarginCss,
  firstRowCss,
  atLocationCss,
  statsAndContentCss,
} from './index.styles';

const isDOMElementVisible = dom => get(dom, 'offsetParent') !== null;

class LocationHeader extends SafePureComponent {
  formLgRef = React.createRef();
  formMdRef = React.createRef();

  componentDidMount() {
    window.addEventListener('scroll', this.onScrollHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScrollHandler = throttle(() => {
    const { stickyBarContext } = this.props;
    const button = this.getFormSecondaryButtonElement();

    if (!button) return;

    const rect = button.getBoundingClientRect();
    const bottom = rect.bottom;

    if (bottom < 0) {
      stickyBarContext.showStickyBar();
    } else {
      stickyBarContext.hideStickyBar();
    }
  }, 80);

  getFormSecondaryButtonElement() {
    const { formLgRef, formMdRef } = this;
    const [visibleButton] = [formLgRef, formMdRef]
      .map(ref => get(ref, 'current.secondaryButtonRef.current'))
      .filter(isDOMElementVisible);

    return visibleButton;
  }

  safeRender() {
    const { props } = this;

    return (
      <Spacer
        marginCss={containerMarginCss}
        containerProps={{
          'data-hero': true,
          'data-sticky-id': get(props, 'rendering.uid'),
        }}
      >
        <Helmet>
          <script type="application/ld+json">
            {get(props, 'fields.locationSEO')}
          </script>
        </Helmet>

        <Container>
          <Row css={firstRowCss}>
            <Column css={showElementCss([MediaQueries.xl])} cols={{ xl: 1 }} />
            <Column cols={{ sm: 6, md: 8, lg: 5, xl: 4 }}>
              <Headline {...props} />
              <div css={showElementCss(MediaQueries.lgAndUp)}>
                <Solutions {...props} />
                <Form {...props} formRef={this.formLgRef} />
                <Content {...props} />
              </div>
            </Column>
            <Column
              css={hideElementCss(MediaQueries.lgAndUp)}
              cols={{ sm: 6, md: 5 }}
            >
              <Solutions {...props} />
            </Column>

            <Column
              css={hideElementCss([MediaQueries.sm, MediaQueries.lgAndUp])}
              cols={{ md: 3 }}
            >
              <Form {...props} formRef={this.formMdRef} />
            </Column>
            <Column noGutter cols={{ sm: 6, md: 8, lg: 7 }}>
              <ImageGallery {...props} />
              <Column
                css={[atLocationCss, showElementCss(MediaQueries.lgAndUp)]}
                cols={{ lg: [4, 7] }}
                offsets={{ lg: [3, 7] }}
              >
                <Stats {...props} />
              </Column>
            </Column>
          </Row>

          <Row css={[statsAndContentCss, hideElementCss(MediaQueries.lgAndUp)]}>
            <Column cols={{ sm: 6, md: 3 }}>
              <Stats {...props} />
            </Column>

            <Column cols={{ sm: 6, md: 5 }}>
              <Content {...props} />
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

LocationHeader.displayName = 'LocationHeader';

export default withConsumer(
  StickyBarConsumer,
  'stickyBarContext',
)(LocationHeader);
